'use client';

import {
  createContext,
  type Dispatch,
  type ElementRef,
  Fragment,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import Image from 'next/image';

import ReactPlayer from 'react-player/lazy';
import Zoom from 'react-zoom-image-hover';
import { useBoolean, useEventListener, useMediaQuery } from 'usehooks-ts';

import {
  Badge,
  Button,
  CaretLeftIcon,
  CaretRightIcon,
  Dialog,
  DialogContent,
  DialogFooter,
  PlayIcon,
  ScrollArea,
  Skeleton,
  TypographyMuted,
} from '@/components';
import { useProductValue } from '@/context';
import {
  BREAKPOINT,
  cn,
  formatNumber,
  generateFileUrl,
  generateThumbnailUrl,
  PATHNAME,
} from '@/utils';

const {
  QUERY_PARAM: {
    SYMBOL: { PLUS: PLUS_SYMBOL },
  },
} = PATHNAME;
const {
  LARGER_THAN: { XL: LARGER_THAN_XL },
} = BREAKPOINT;

type ProductGalleryContextProps = Record<'open', boolean> &
  Record<'setOpen', (open: boolean) => void> &
  Record<'openGallery', (index: number) => void> &
  Partial<
    Record<'targetIndex', number> &
      Record<'setTargetIndex', Dispatch<SetStateAction<number>>>
  >;

const ProductGalleryContext = createContext<ProductGalleryContextProps>({
  open: false,
  setOpen: open => {},
  openGallery: index => {},
});

const useProductGallery = () => {
  const context = useContext(ProductGalleryContext);
  if (!context) {
    throw new Error(
      'useProductGallery must be used within <ProductGalleryContext />',
    );
  }
  return context;
};

const ProductGalleryPreview = () => {
  const {
    open,
    setOpen,
    setTargetIndex,
    targetIndex = 0,
  } = useProductGallery();

  const { value: zoom, setValue: setZoom } = useBoolean();
  const { pictures = [] } = useProductValue();

  const scrollableContainer = useRef<ElementRef<typeof ScrollArea>>(null);

  useEventListener('keydown', event => {
    if (event.key === 'ArrowRight') {
      handlePrevIndex();
    }
    if (event.key === 'ArrowLeft') {
      handleNextIndex();
    }
  });

  useEffect(() => {
    const container = scrollableContainer.current;
    if (container) {
      const targetThumbnail = container.querySelector(
        `[data-index="${targetIndex}"]`,
      );
      if (targetThumbnail) {
        targetThumbnail.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    }
  }, [targetIndex]);

  const handlePrevIndex = () => {
    if (targetIndex > 0) {
      setTargetIndex?.(prevIndex => prevIndex - 1);
    }
  };

  const handleNextIndex = () => {
    if (targetIndex < pictures.length - 1) {
      setTargetIndex?.(prevIndex => prevIndex + 1);
    }
  };

  const { thumbnailWidth, thumbnailHeight, highlightWidth, highlightHeight } = {
    thumbnailWidth: 95,
    thumbnailHeight: 90,
    highlightWidth: 1200,
    highlightHeight: 650,
  } as const;

  const disabledPrev = targetIndex === 0;
  const disabledNext = targetIndex === pictures.length - 1;
  const { id, title, type } = pictures[targetIndex];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {pictures.length > 0 ? (
        <DialogContent
          variant='full'
          className='flex flex-col justify-center bg-black/75 p-0 [&>button>svg]:size-8 [&>button>svg]:text-background [&>button]:p-2'
          onClick={() => setOpen(false)}
        >
          <div className='absolute inset-x-0 flex items-center justify-center gap-2 overflow-hidden p-2'>
            <div className='absolute inset-0 -z-10' />
            <Button
              tabIndex={-1}
              variant='outline'
              aria-disabled={disabledPrev}
              data-disabled={disabledPrev}
              className='absolute right-2 top-1/2 z-10 h-auto -translate-y-1/2 p-3 data-[disabled=true]:opacity-50'
              onClick={event => {
                event.stopPropagation();
                handlePrevIndex();
              }}
            >
              <CaretRightIcon />
            </Button>
            <figure onClick={event => event.stopPropagation()}>
              {type === 'Video' ? (
                <ReactPlayer
                  controls
                  url={generateFileUrl(
                    'Product',
                    id,
                    highlightWidth,
                    highlightHeight,
                  )}
                  width={'100%'}
                  height={'100%'}
                  className='cursor-pointer'
                />
              ) : (
                <Image
                  src={generateFileUrl(
                    'Product',
                    id,
                    highlightWidth,
                    highlightHeight,
                  )}
                  width={highlightWidth}
                  height={highlightHeight}
                  data-zoomed-in={zoom}
                  alt={title}
                  className='size-full max-h-[70dvh] cursor-zoom-in object-contain transition data-[zoomed-in=true]:cursor-zoom-out max-lg:px-8 lg:max-w-[calc(100vw/2)] lg:scale-75 lg:data-[zoomed-in=true]:scale-100'
                  onClick={() => setZoom(prev => !prev)}
                />
              )}
            </figure>
            <Button
              variant='outline'
              aria-disabled={disabledNext}
              data-disabled={disabledNext}
              className='absolute left-2 top-1/2 z-10 h-auto -translate-y-1/2 p-3 data-[disabled=true]:opacity-50'
              onClick={event => {
                event.stopPropagation();
                handleNextIndex();
              }}
            >
              <CaretLeftIcon />
            </Button>
          </div>
          <DialogFooter
            className='mt-auto bg-gradient-to-t from-foreground/80 to-transparent'
            onClick={event => event.stopPropagation()}
          >
            <ScrollArea orientation='horizontal'>
              <div ref={scrollableContainer} className='flex gap-4 p-2'>
                {pictures.map(({ id, title, type }, i) => {
                  const src =
                    type === 'Video'
                      ? generateThumbnailUrl('Product', id)
                      : generateFileUrl(
                          'Product',
                          id,
                          thumbnailWidth,
                          thumbnailHeight,
                        );

                  return (
                    <figure
                      key={id}
                      data-index={i}
                      data-active={targetIndex === i}
                      className={cn(
                        'relative flex items-center justify-center',
                        'size-24 shrink-0 cursor-pointer rounded-md border',
                        'border-transparent p-2 transition',
                        'data-[active=true]:border-background',
                      )}
                      onClick={() => setTargetIndex?.(i)}
                    >
                      <Image
                        src={src}
                        alt={title}
                        width={thumbnailWidth}
                        height={thumbnailHeight}
                        className='size-full rounded-sm object-contain'
                      />
                      {type === 'Video' ? (
                        <PlayIcon
                          weight='fill'
                          className='absolute text-background'
                        />
                      ) : null}
                    </figure>
                  );
                })}
              </div>
            </ScrollArea>
          </DialogFooter>
        </DialogContent>
      ) : null}
    </Dialog>
  );
};

const ProductGalleryBanner = () => {
  const { openGallery } = useProductGallery();
  const { pictures = [] } = useProductValue();

  const bannerPicture = pictures.at(0);

  return (
    <div className='relative mb-5 flex h-[380px] cursor-pointer items-center justify-center overflow-hidden rounded-md border bg-background'>
      <div className='size-full' onClick={() => openGallery(0)}>
        {bannerPicture ? (
          bannerPicture.type === 'Video' ? (
            <ReactPlayer
              playing
              url={generateFileUrl('Product', bannerPicture.id)}
              width='100%'
              height='100%'
            />
          ) : (
            <Zoom
              className='size-full p-10'
              src={generateFileUrl('Product', bannerPicture.id, 1000, 1000)}
              width='100%'
              height='100%'
              zoomScale={2}
            />
          )
        ) : null}
      </div>
    </div>
  );
};

const ProductGalleryThumbnails = () => {
  const { pictures = [] } = useProductValue();
  const { openGallery } = useProductGallery();

  const matches = useMediaQuery(LARGER_THAN_XL);

  const thumbnailsCount = matches ? 4 : 3;
  const hasThumbnails = pictures.length > 1;
  const showMoreThumbnails = pictures.length > thumbnailsCount;
  const thumbnails = pictures.slice(1, thumbnailsCount);

  const { width, height } = { width: 95, height: 90 };

  if (hasThumbnails) {
    return (
      <div
        data-expandable={pictures.length > thumbnailsCount}
        className='lg:data-[expandable=true]::justify-start flex items-center justify-center gap-4 *:size-24 *:shrink max-sm:*:size-20 lg:data-[expandable=true]:justify-between'
      >
        {thumbnails.map(({ id, title, type }, i) => {
          const isVideo = type === 'Video';
          const src = isVideo
            ? generateThumbnailUrl('Product', id)
            : generateFileUrl('Product', id, width, height);

          return (
            <Button
              key={id}
              variant='outline'
              className='p-2'
              onClick={() => openGallery(i + 1)}
            >
              <Image
                src={src}
                alt={title}
                width={width}
                height={height}
                className='size-full rounded-sm object-contain'
              />
              {isVideo ? (
                <PlayIcon weight='fill' className='absolute text-background' />
              ) : null}
            </Button>
          );
        })}

        {showMoreThumbnails ? (
          <div
            className='flex cursor-pointer items-center justify-center rounded-md bg-muted'
            onClick={() => openGallery(0)}
          >
            <TypographyMuted className='text-lg font-bold text-muted-foreground'>
              {formatNumber(
                pictures.length - thumbnailsCount,
                undefined,
                PLUS_SYMBOL,
              )}
            </TypographyMuted>
          </div>
        ) : null}
      </div>
    );
  }
};

const ProductGalleryPlaceholder = () => {
  return (
    <div className='flex-1 sm:col-span-4'>
      <Skeleton className='relative mb-5 h-[380px] w-full' />
      <div className='hidden h-[90px] justify-center gap-4 sm:flex lg:justify-between'>
        <Skeleton className='w-[95px]' />
        <Skeleton className='w-[95px]' />
        <Skeleton className='w-[95px]' />
        <Skeleton className='w-[95px]' />
      </div>
    </div>
  );
};

const ProductGallery = () => {
  const { pictures = [], specialOffer } = useProductValue();
  const { value: open, setValue: setOpen } = useBoolean();

  const [targetIndex, setTargetIndex] = useState(0);

  const isExistedPictures = pictures.length > 0;

  const openGallery = (index: number) => {
    setTargetIndex(index);
    setOpen(true);
  };

  return (
    <ProductGalleryContext.Provider
      value={{
        open,
        targetIndex,
        setOpen,
        openGallery,
        setTargetIndex,
      }}
    >
      <div className='relative flex-1 sm:col-span-4'>
        {isExistedPictures ? (
          <Fragment>
            <ProductGalleryPreview />
            <ProductGalleryBanner />
            <ProductGalleryThumbnails />
          </Fragment>
        ) : (
          <ProductGalleryPlaceholder />
        )}

        {specialOffer ? (
          <Badge
            variant='danger'
            className='absolute end-0 top-0 z-10 m-4 p-1.5 lg:hidden'
          >
            فروش ویژه
          </Badge>
        ) : null}
      </div>
    </ProductGalleryContext.Provider>
  );
};

export { ProductGallery as ProductGallery };
